import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  HostListener,
  AfterViewInit,
  ElementRef,
  PLATFORM_ID,
  Inject,
  ViewChild,
  signal,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { RouterLink, RouterLinkActive, Router } from '@angular/router';
import { ButtonComponent } from '../button/button.component';
import { ContactPopupService } from '../../../services/contact-popup.service';
import { NavigationService } from '../../../services/navigation.service';
import {
  trigger,
  state,
  style,
  transition,
  animate,
} from '@angular/animations';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive, ButtonComponent],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  animations: [
    trigger('slideInFromLeft', [
      transition(':enter', [
        style({
          transform: 'translateX(-100%)',
          opacity: 0,
        }),
        animate(
          '1200ms ease-out',
          style({
            transform: 'translateX(0)',
            opacity: 1,
          })
        ),
      ]),
    ]),
    trigger('fadeOut', [
      state(
        'visible',
        style({
          opacity: 1,
          transform: 'scale(1)',
        })
      ),
      state(
        'fading',
        style({
          opacity: 0.6,
          transform: 'scale(1.05)',
        })
      ),
      transition('visible => fading', [animate('1200ms ease-out')]),
      transition('fading => visible', [animate('1200ms ease-out')]),
    ]),
  ],
})
export class HeaderComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() button1Text: string | null | undefined = null;
  @Input() button1Id: string | null | undefined = null;
  @Input() button2Text: string | null | undefined = null;
  @Input() headTitle1: string | null | undefined = '';
  @Input() headTitle2_2: string | null | undefined = '';
  @Input() headTitle3: string | null | undefined = '';
  @Input() headTitleLibre: string | null | undefined = null;
  @Input() headTitle2Font: string | null | undefined = null;
  @Input() description1: string | null | undefined = '';
  @Input() positionLine: string | null | undefined = 'top';
  @Input() description2: string | null | undefined = '';
  @Input() imagePath: string | null | undefined = null;
  @Input() imagePaths: string[] = [];
  @Input() navigateTo: string | null | undefined = null;
  @Input() littleImagePath: string | null | undefined = null;
  componentLoaded = signal(false);
  @ViewChild('animateElement') animateElement?: ElementRef;

  public isInView: boolean = false;

  currentPath: string = '/home';
  isMobileMenuOpen: boolean = false;
  isContactPopupOpen: boolean = false;
  isScrolled: boolean = false;
  isBrowser: boolean;

  currentImageIndex: number = 0;
  carouselInterval: any;
  isTransitioning: boolean = false;
  navbarItems = [
    { name: 'Home', link: '/home' },
    { name: 'Cladding and Doors', link: '/cladding-and-doors' },
    { name: 'Furniture', link: '/furniture' },
    { name: 'Lighting', link: '/lighting' },
    { name: `Meet Jafy's`, link: '/about' },
    { name: 'Projects', link: '/projects' },
  ];
  constructor(
    private router: Router,
    private elementRef: ElementRef,
    private contactPopupService: ContactPopupService,
    private navigationService: NavigationService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }
  ngOnInit(): void {
    this.isScrolled = window.scrollY > 10;
    this.router.events.subscribe(() => {
      this.currentPath = this.router.url;
    });
    if (this.isBrowser) {
      this.preOptimizeLcpElements();
      this.initImageCarousel();
    }
  }

  ngAfterViewInit(): void {
    if (this.isBrowser) {
      this.optimizeLcpElements();
    }
    this.componentLoaded.set(true);
  }

  private initImageCarousel(): void {
    if (this.imagePaths && this.imagePaths.length > 1) {
      this.carouselInterval = setInterval(() => {
        this.nextImage();
      }, 4000);
    }
  }
  private nextImage(): void {
    if (
      !this.imagePaths ||
      this.imagePaths.length <= 1 ||
      this.isTransitioning
    ) {
      return;
    }

    this.isTransitioning = true;

    setTimeout(() => {
      this.currentImageIndex =
        (this.currentImageIndex + 1) % this.imagePaths.length;
      this.isTransitioning = false;
    }, 1200);
  }

  getCurrentImagePath(): string {
    if (this.imagePaths && this.imagePaths.length > 0) {
      return this.imagePaths[this.currentImageIndex];
    }
    return this.imagePath || '';
  }

  getNextImagePath(): string {
    if (this.imagePaths && this.imagePaths.length > 1) {
      const nextIndex = (this.currentImageIndex + 1) % this.imagePaths.length;
      return this.imagePaths[nextIndex];
    }
    return '';
  }

  ngOnDestroy(): void {
    if (this.carouselInterval) {
      clearInterval(this.carouselInterval);
    }
  }

  private preOptimizeLcpElements(): void {
    if (this.headTitle1 || this.headTitle3 || this.headTitleLibre) {
      document.fonts.ready.then(() => {});
    }
  }

  private optimizeLcpElements(): void {
    const lcpElements = this.elementRef.nativeElement.querySelectorAll(
      '[data-lcp-element="true"]'
    );

    if (lcpElements.length > 0) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.setAttribute('importance', 'high');
              observer.disconnect();
            }
          });
        },
        { threshold: 0.1 }
      );

      lcpElements.forEach((element: Element) => observer.observe(element));
    }
  }
  openContactPopup(): void {
    if (this.isMobileMenuOpen) {
      this.closeMobileMenu();
    }

    this.contactPopupService.openPopup();
  }
  handleButton1Click(): void {
    if (
      this.button1Id === 'upload-design' ||
      this.button1Id === 'start-customizing'
    ) {
      this.openContactPopup();
    } else if (this.button1Id === 'explore-lighting-collection') {
      this.navigationService.navigateToLightingProjects();
    }
  }
  toggleMobileMenu(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;

    if (this.isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      document.body.classList.add('mobile-menu-open');
    } else {
      document.body.style.overflow = '';
      document.body.classList.remove('mobile-menu-open');
    }
  }

  closeMobileMenu(): void {
    if (this.isMobileMenuOpen) {
      this.isMobileMenuOpen = false;
      document.body.style.overflow = '';
      document.body.classList.remove('mobile-menu-open');
    }
  }
  @HostListener('document:keydown.escape')
  onKeydownHandler() {
    if (this.isMobileMenuOpen) {
      this.toggleMobileMenu();
    }
    this.contactPopupService.closePopup();
  }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    this.isScrolled = window.scrollY > 10;
  }

  isActive(route: string): boolean {
    if (route === '/home') {
      return this.router.url === route;
    }
    return this.router.url.startsWith(route);
  }
}
